import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { bustSettingsCache } from '../../utils/useSettings';

// ── Shared helpers ────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
    <div className="px-4 py-3 border-bottom" style={{ background: 'var(--page-bg)' }}>
      <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '16px' }}>{title}</h6>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SaveBar = ({ saving, saved, error, onSave, label = 'Save Changes' }) => (
  <div className="d-flex align-items-center gap-3 mt-3">
    <button className="btn btn-dream rounded-pill px-4" onClick={onSave} disabled={saving}>
      {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : <><i className="bi bi-check2 me-2"></i>{label}</>}
    </button>
    {saved  && <span style={{ color: '#059669', fontSize: '13px', fontWeight: 600 }}><i className="bi bi-check-circle-fill me-1"></i>Saved!</span>}
    {error  && <span style={{ color: '#dc3545', fontSize: '13px' }}>{error}</span>}
  </div>
);

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'landing',    icon: 'bi-house',          label: 'Landing Page'    },
  { id: 'story',      icon: 'bi-book',           label: 'Story Options'   },
  { id: 'flags',      icon: 'bi-toggles',        label: 'Feature Flags'   },
  { id: 'limits',     icon: 'bi-sliders',        label: 'Plans & Limits'  },
  { id: 'business',   icon: 'bi-gear',           label: 'Business'        },
  { id: 'affiliates', icon: 'bi-megaphone-fill', label: 'Affiliates'      },
  { id: 'pricing',    icon: 'bi-currency-dollar',label: 'Subscription $'  },
  { id: 'ratelimits', icon: 'bi-shield-lock',    label: 'Rate Limits'     },
  { id: 'cron',       icon: 'bi-clock-history',  label: 'Cron Jobs'       },
];

// Human-readable window label for a millisecond value (e.g. 60000 → "1m").
const msLabel = (ms) => {
  if (!Number.isFinite(ms)) return '';
  if (ms % 86400000 === 0) return `${ms / 86400000}d`;
  if (ms % 3600000 === 0)  return `${ms / 3600000}h`;
  if (ms % 60000 === 0)    return `${ms / 60000}m`;
  if (ms % 1000 === 0)     return `${ms / 1000}s`;
  return `${ms}ms`;
};

const SUB_PRICE_ROWS = [
  { key: 'basicMonthly',  label: 'Basic — Monthly' },
  { key: 'basicAnnual',   label: 'Basic — Annual' },
  { key: 'familyMonthly', label: 'Family — Monthly' },
  { key: 'familyAnnual',  label: 'Family — Annual' },
  { key: 'gift',          label: 'Gift Story' },
];

// Plan-limit table rows (key in the planLimits object → display label)
const LIMIT_ROWS = [
  { key: 'stories',            label: 'Stories' },
  { key: 'narrations',         label: 'Narrations' },
  { key: 'cloneNarrations',    label: 'Voice (clone) Narrations' },
  { key: 'regens',             label: 'Illustration Regens' },
  { key: 'photoIllustrations', label: 'Photo Illustrations' },
];

// Top-up pack metadata (display only — usdPrice + grant are edited)
const PACK_META = {
  story:     { emoji: '📖', label: 'Story Pack',              noun: 'stories' },
  narration: { emoji: '🔊', label: 'Narration Pack',          noun: 'narrations' },
  voice:     { emoji: '🎙️', label: 'Voice Pack',              noun: 'clone narrations' },
  regen:     { emoji: '🎨', label: 'Illustration Pack',       noun: 'regens' },
  photo:     { emoji: '📸', label: 'Photo Illustration Pack', noun: 'photo illustrations' },
};
const PACK_IDS = ['story', 'narration', 'voice', 'regen', 'photo'];

const AI_CAP_ROWS = [
  { key: 'preview',         label: 'Landing preview stories' },
  { key: 'recommendations', label: 'Story recommendations' },
  { key: 'recap',           label: 'Welcome-back recaps' },
  { key: 'email',           label: 'Personalised email snippets' },
];

const EMPTY_LIMITS = {
  free:   { stories: 0, narrations: 0, cloneNarrations: 0, regens: 0, photoIllustrations: 0 },
  basic:  { stories: 0, narrations: 0, cloneNarrations: 0, regens: 0, photoIllustrations: 0 },
  family: { stories: 0, narrations: 0, cloneNarrations: 0, regens: 0, photoIllustrations: 0 },
};

// ── AdminSettings ─────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('landing');
  const [loading,   setLoading]   = useState(true);
  // Never render this form on unloaded state. Every field below is seeded from
  // a useState default (planLimits starts at EMPTY_LIMITS — all zeros), so if
  // the GET fails the admin is shown a normal-looking settings page whose every
  // limit reads 0. Saving any section then persists those zeros: the backend's
  // intOr(0, 0, 1000000, default) keeps 0, because 0 is a legitimate value for
  // some limits and it cannot tell "the admin meant zero" from "the form never
  // loaded". That is a site-wide lockout — nobody can generate, narrate or
  // illustrate — triggered by a transient 500 plus one Save click.
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [envConfig, setEnvConfig] = useState(null);

  // Editable state for each section
  const [stats,        setStats]        = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [features,     setFeatures]     = useState([]);
  const [tones,        setTones]        = useState([]);
  const [languages,    setLanguages]    = useState([]);
  const [interests,    setInterests]    = useState([]);
  const [learnCats,    setLearnCats]    = useState([]);
  const [themeCats,    setThemeCats]    = useState([]);
  const [featureFlags,      setFeatureFlags]      = useState({});
  const [maintenance,       setMaintenance]       = useState({ enabled: false, message: '' });
  const [contactTime,       setContactTime]       = useState('24–48 hours');
  const [affiliateDefaults, setAffiliateDefaults] = useState({ commissionPercent: 20, payoutThreshold: 20 });
  // Plans & Limits + Security (admin-managed operational config)
  const [planLimits,    setPlanLimits]    = useState(EMPTY_LIMITS);
  const [childLimits,   setChildLimits]   = useState({ free: 1, basic: 3 });
  const [topUpConfig,   setTopUpConfig]   = useState({});
  const [aiCaps,        setAiCaps]        = useState({ preview: 300, recommendations: 300, recap: 300, email: 500 });
  const [otpExpiry,     setOtpExpiry]     = useState(10);
  const [reviewerBypass,setReviewerBypass]= useState([]);
  const [subPricing,    setSubPricing]    = useState({ basicMonthly: 14, familyMonthly: 22, gift: 7.99, basicAnnual: 154, familyAnnual: 242 });
  const [trialConfig,   setTrialConfig]   = useState({ enabled: true, days: 7, plans: ['basic', 'family'], requiresCard: true });
  const [rateLimitsCfg, setRateLimitsCfg] = useState({});
  const [cronCfg,       setCronCfg]       = useState({});

  // Per-section save state
  const [saves, setSaves] = useState({});
  const setSave = (key, val) => setSaves(p => ({ ...p, [key]: val }));

  useEffect(() => {
    API.get('/admin/settings')
      .then(res => {
        const rows = res.data.settings || [];
        const get  = (key, fallback) => rows.find(r => r.key === key)?.value ?? fallback;
        setStats(get('landingStats', []));
        setTestimonials(get('testimonials', []));
        setFeatures(get('landingFeatures', []));
        setTones(get('storyTones', []));
        setLanguages(get('storyLanguages', []));
        setInterests(get('storyInterests', []));
        setLearnCats(get('learningCategories', []));
        setThemeCats(get('themeCategories', []));
        setFeatureFlags(get('featureFlags', {}));
        setMaintenance(get('maintenanceMode', { enabled: false, message: '' }));
        setContactTime(get('contactResponseTime', '24–48 hours'));
        setAffiliateDefaults(get('affiliateDefaults', { commissionPercent: 20, payoutThreshold: 20 }));
        setPlanLimits(get('planLimits', EMPTY_LIMITS));
        setChildLimits(get('childProfileLimits', { free: 1, basic: 3 }));
        setTopUpConfig(get('topUpConfig', {}));
        setAiCaps(get('aiDailyCaps', { preview: 300, recommendations: 300, recap: 300, email: 500 }));
        setOtpExpiry(get('otpExpiryMinutes', 10));
        setReviewerBypass(get('reviewerBypass', []));
        setSubPricing(get('subscriptionPricing', { basicMonthly: 14, familyMonthly: 22, gift: 7.99, basicAnnual: 154, familyAnnual: 242 }));
        const tc = get('trialConfig', { enabled: true, days: 7, plans: ['basic', 'family'], requiresCard: true });
        const tcPlans = Array.isArray(tc.plans) ? tc.plans : (tc.plan ? [tc.plan] : ['family']);
        setTrialConfig({ enabled: tc.enabled !== false, days: tc.days || 7, plans: tcPlans, requiresCard: tc.requiresCard !== false });
        setRateLimitsCfg(get('rateLimits', {}));
        setCronCfg(get('cronSchedules', {}));
        setEnvConfig(res.data.envConfig || null);
      })
      .catch((err) => {
        setLoadError(err.response?.data?.message || 'Could not load settings. Nothing has been changed.');
      })
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const save = useCallback(async (key, value, label) => {
    setSave(key, { saving: true, saved: false, error: '' });
    try {
      await API.put('/admin/settings', { settings: [{ key, value, label }] });
      bustSettingsCache();
      setSave(key, { saving: false, saved: true, error: '' });
      setTimeout(() => setSave(key, {}), 3000);
    } catch (err) {
      setSave(key, { saving: false, saved: false, error: err.response?.data?.message || 'Save failed.' });
    }
  }, []);

  const s = (key) => saves[key] || {};

  if (loading) return (
    <AdminLayout title="Site Settings">
      <div className="text-center py-5"><span className="spinner-border" style={{ color: '#6f42c1' }}></span></div>
    </AdminLayout>
  );

  // Show the failure and withhold the form, rather than a page of zeros that
  // looks authoritative and overwrites real config on the next Save.
  if (loadError) return (
    <AdminLayout title="Site Settings">
      <div className="text-center py-5">
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
        <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Settings could not be loaded</h5>
        <p className="mb-1" style={{ color: 'var(--text-muted2)' }}>{loadError}</p>
        <p className="small mb-4" style={{ color: 'var(--text-soft)' }}>
          The form is hidden on purpose — saving it now would overwrite your live configuration with blank values.
        </p>
        <button className="btn btn-dream rounded-pill px-4" onClick={() => { setLoadError(''); setLoading(true); setReloadKey(k => k + 1); }}>
          Try again
        </button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Site Settings">
      {/* ── Tab bar ─────────────────────────────────────── */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="btn btn-sm rounded-pill"
            style={{
              background:  activeTab === t.id ? '#6f42c1' : '#f0e8ff',
              color:       activeTab === t.id ? '#fff'    : '#6f42c1',
              border:      'none',
              fontWeight:  activeTab === t.id ? 700 : 400,
              fontSize:    '13px',
              padding:     '6px 16px',
            }}>
            <i className={`bi ${t.icon} me-2`}></i>{t.label}
          </button>
        ))}
      </div>

      {/* ══ LANDING PAGE ══════════════════════════════════════ */}
      {activeTab === 'landing' && <>
        {/* Stats */}
        <Section title="🏠 Stats Bar">
          <p className="text-muted small mb-3">The four numbers shown in the social-proof bar under the hero. Changes go live instantly.</p>
          {stats.map((s, i) => (
            <div key={i} className="d-flex align-items-center gap-2 mb-2">
              <input className="form-control form-control-sm" style={{ maxWidth: '110px', fontWeight: 700, color: 'var(--text-main)' }}
                placeholder="10+" value={s.stat}
                onChange={e => setStats(p => p.map((x,j) => j===i ? {...x, stat: e.target.value} : x))} />
              <input className="form-control form-control-sm flex-grow-1" placeholder="Label"
                value={s.label}
                onChange={e => setStats(p => p.map((x,j) => j===i ? {...x, label: e.target.value} : x))} />
              <button className="btn btn-sm btn-outline-danger rounded-2" onClick={() => setStats(p => p.filter((_,j) => j!==i))}>
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-2" onClick={() => setStats(p => [...p, { stat: '', label: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Stat
          </button>
          <SaveBar {...s('landingStats')} onSave={() => save('landingStats', stats, 'Landing Page — Stats Bar')} />
        </Section>

        {/* Testimonials */}
        <Section title="💬 Testimonials">
          <p className="text-muted small mb-3">Edit or reorder customer testimonials shown on the landing page.</p>
          {testimonials.map((t, i) => (
            <div key={i} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: '12px', background: 'var(--header-bg)' }}>
              <div className="d-flex gap-2 mb-2">
                <input className="form-control form-control-sm" style={{ maxWidth: '140px' }} placeholder="Name"
                  value={t.name} onChange={e => setTestimonials(p => p.map((x,j) => j===i ? {...x, name: e.target.value} : x))} />
                <input className="form-control form-control-sm flex-grow-1" placeholder="Role e.g. Mom of 2, London"
                  value={t.role} onChange={e => setTestimonials(p => p.map((x,j) => j===i ? {...x, role: e.target.value} : x))} />
                <input className="form-control form-control-sm" style={{ maxWidth: '56px' }} placeholder="A"
                  value={t.avatar} onChange={e => setTestimonials(p => p.map((x,j) => j===i ? {...x, avatar: e.target.value} : x))} />
                <input type="color" className="form-control form-control-sm form-control-color" style={{ maxWidth: '44px' }}
                  value={t.color} onChange={e => setTestimonials(p => p.map((x,j) => j===i ? {...x, color: e.target.value} : x))} />
                <button className="btn btn-sm btn-outline-danger rounded-2 flex-shrink-0"
                  onClick={() => setTestimonials(p => p.filter((_,j) => j!==i))}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              <textarea className="form-control form-control-sm" rows={2} placeholder="Quote"
                value={t.quote} onChange={e => setTestimonials(p => p.map((x,j) => j===i ? {...x, quote: e.target.value} : x))} />
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-1"
            onClick={() => setTestimonials(p => [...p, { name: '', role: '', avatar: '', color: '#6f42c1', quote: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Testimonial
          </button>
          <SaveBar {...s('testimonials')} onSave={() => save('testimonials', testimonials, 'Landing Page — Testimonials')} />
        </Section>

        {/* Features */}
        <Section title="✨ Features Grid">
          <p className="text-muted small mb-3">Feature cards shown below the how-it-works section. Icon is an emoji.</p>
          {features.map((f, i) => (
            <div key={i} className="d-flex align-items-start gap-2 mb-2">
              <input className="form-control form-control-sm" style={{ maxWidth: '50px' }} placeholder="✨"
                value={f.icon} onChange={e => setFeatures(p => p.map((x,j) => j===i ? {...x, icon: e.target.value} : x))} />
              <input type="color" className="form-control form-control-sm form-control-color" style={{ maxWidth: '44px' }}
                value={f.bg || '#f0e8ff'} onChange={e => setFeatures(p => p.map((x,j) => j===i ? {...x, bg: e.target.value} : x))} />
              <input className="form-control form-control-sm" style={{ maxWidth: '220px' }} placeholder="Title"
                value={f.title} onChange={e => setFeatures(p => p.map((x,j) => j===i ? {...x, title: e.target.value} : x))} />
              <input className="form-control form-control-sm flex-grow-1" placeholder="Description"
                value={f.desc} onChange={e => setFeatures(p => p.map((x,j) => j===i ? {...x, desc: e.target.value} : x))} />
              <button className="btn btn-sm btn-outline-danger rounded-2 flex-shrink-0"
                onClick={() => setFeatures(p => p.filter((_,j) => j!==i))}>
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-2"
            onClick={() => setFeatures(p => [...p, { icon: '', bg: '#f0e8ff', title: '', desc: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Feature
          </button>
          <SaveBar {...s('landingFeatures')} onSave={() => save('landingFeatures', features, 'Landing Page — Features Grid')} />
        </Section>
      </>}

      {/* ══ STORY OPTIONS ═════════════════════════════════════ */}
      {activeTab === 'story' && <>
        {/* Tones */}
        <Section title="🎭 Story Tones">
          <p className="text-muted small mb-3">Tone options shown in the story generator. <code>value</code> is sent to the backend.</p>
          {tones.map((t, i) => (
            <div key={i} className="d-flex align-items-center gap-2 mb-2">
              <input className="form-control form-control-sm" style={{ maxWidth: '120px' }} placeholder="value (e.g. magical)"
                value={t.value} onChange={e => setTones(p => p.map((x,j) => j===i ? {...x, value: e.target.value} : x))} />
              <input className="form-control form-control-sm" style={{ maxWidth: '54px' }} placeholder="✨"
                value={t.icon}  onChange={e => setTones(p => p.map((x,j) => j===i ? {...x, icon: e.target.value} : x))} />
              <input className="form-control form-control-sm flex-grow-1" placeholder="Short description"
                value={t.desc}  onChange={e => setTones(p => p.map((x,j) => j===i ? {...x, desc: e.target.value} : x))} />
              <button className="btn btn-sm btn-outline-danger rounded-2 flex-shrink-0"
                onClick={() => setTones(p => p.filter((_,j) => j!==i))}>
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-2"
            onClick={() => setTones(p => [...p, { value: '', icon: '', desc: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Tone
          </button>
          <SaveBar {...s('storyTones')} onSave={() => save('storyTones', tones, 'Story Generator — Tones')} />
        </Section>

        {/* Languages */}
        <Section title="🌍 Story Languages">
          <p className="text-muted small mb-3"><code>code</code> is the ISO 639-1 code sent to the backend (e.g. <code>en</code>, <code>es</code>).</p>
          {languages.map((l, i) => (
            <div key={i} className="d-flex align-items-center gap-2 mb-2">
              <input className="form-control form-control-sm" style={{ maxWidth: '70px' }} placeholder="en"
                value={l.code}  onChange={e => setLanguages(p => p.map((x,j) => j===i ? {...x, code: e.target.value} : x))} />
              <input className="form-control form-control-sm" style={{ maxWidth: '54px' }} placeholder="🇬🇧"
                value={l.flag}  onChange={e => setLanguages(p => p.map((x,j) => j===i ? {...x, flag: e.target.value} : x))} />
              <input className="form-control form-control-sm flex-grow-1" placeholder="English"
                value={l.label} onChange={e => setLanguages(p => p.map((x,j) => j===i ? {...x, label: e.target.value} : x))} />
              <button className="btn btn-sm btn-outline-danger rounded-2 flex-shrink-0"
                onClick={() => setLanguages(p => p.filter((_,j) => j!==i))}>
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-2"
            onClick={() => setLanguages(p => [...p, { code: '', flag: '', label: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Language
          </button>
          <SaveBar {...s('storyLanguages')} onSave={() => save('storyLanguages', languages, 'Story Generator — Languages')} />
        </Section>

        {/* Interests */}
        <Section title="🦕 Story Interests">
          <p className="text-muted small mb-3">Interest chips shown in the story generator. One per line or comma-separated.</p>
          <textarea className="form-control mb-3" rows={4}
            value={interests.join(', ')}
            onChange={e => setInterests(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="Dinosaurs, Space, Animals, Magic, ..." />
          <div className="d-flex flex-wrap gap-1 mb-2">
            {interests.map(i => (
              <span key={i} className="badge rounded-pill px-3 py-2" style={{ background: 'var(--purple-section)', color: '#4b2d9f', fontSize: '12px' }}>{i}</span>
            ))}
          </div>
          <SaveBar {...s('storyInterests')} onSave={() => save('storyInterests', interests, 'Story Generator — Interests')} />
        </Section>

        {/* Learning Categories */}
        <Section title="📚 Learning Categories">
          <p className="text-muted small mb-3">Categories and topics in the "Learning Topic" tab of the story generator.</p>
          {learnCats.map((cat, ci) => (
            <div key={ci} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: '12px', background: 'var(--header-bg)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <input className="form-control form-control-sm" style={{ maxWidth: '50px' }} placeholder="🔬"
                  value={cat.icon} onChange={e => setLearnCats(p => p.map((x,j) => j===ci ? {...x, icon: e.target.value} : x))} />
                <input className="form-control form-control-sm flex-grow-1" placeholder="Category name"
                  value={cat.label} onChange={e => setLearnCats(p => p.map((x,j) => j===ci ? {...x, label: e.target.value} : x))} />
                <button className="btn btn-sm btn-outline-danger rounded-2"
                  onClick={() => setLearnCats(p => p.filter((_,j) => j!==ci))}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              <textarea className="form-control form-control-sm" rows={2}
                placeholder="Topics (comma-separated)"
                value={(cat.topics || []).join(', ')}
                onChange={e => setLearnCats(p => p.map((x,j) => j===ci
                  ? {...x, topics: e.target.value.split(',').map(t => t.trim()).filter(Boolean)}
                  : x))} />
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-1"
            onClick={() => setLearnCats(p => [...p, { label: '', icon: '', topics: [] }])}>
            <i className="bi bi-plus me-1"></i>Add Category
          </button>
          <SaveBar {...s('learningCategories')} onSave={() => save('learningCategories', learnCats, 'Story Generator — Learning Categories')} />
        </Section>

        {/* Theme Categories */}
        <Section title="🎨 Theme Categories">
          <p className="text-muted small mb-3">Tabs in the "Theme" section of the story generator (e.g. Life Lessons, Seasonal, Emotional).</p>
          {themeCats.map((cat, ci) => (
            <div key={ci} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: '12px', background: 'var(--header-bg)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <input className="form-control form-control-sm" style={{ maxWidth: '50px' }} placeholder="💡"
                  value={cat.icon} onChange={e => setThemeCats(p => p.map((x,j) => j===ci ? {...x, icon: e.target.value} : x))} />
                <input className="form-control form-control-sm" style={{ maxWidth: '180px' }} placeholder="Category name"
                  value={cat.label} onChange={e => setThemeCats(p => p.map((x,j) => j===ci ? {...x, label: e.target.value} : x))} />
                <input className="form-control form-control-sm flex-grow-1" placeholder="Short description"
                  value={cat.desc} onChange={e => setThemeCats(p => p.map((x,j) => j===ci ? {...x, desc: e.target.value} : x))} />
                <button className="btn btn-sm btn-outline-danger rounded-2"
                  onClick={() => setThemeCats(p => p.filter((_,j) => j!==ci))}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              <textarea className="form-control form-control-sm" rows={2}
                placeholder="Themes (comma-separated)"
                value={(cat.themes || []).join(', ')}
                onChange={e => setThemeCats(p => p.map((x,j) => j===ci
                  ? {...x, themes: e.target.value.split(',').map(t => t.trim()).filter(Boolean)}
                  : x))} />
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-1"
            onClick={() => setThemeCats(p => [...p, { label: '', icon: '', desc: '', themes: [] }])}>
            <i className="bi bi-plus me-1"></i>Add Category
          </button>
          <SaveBar {...s('themeCategories')} onSave={() => save('themeCategories', themeCats, 'Story Generator — Theme Categories')} />
        </Section>
      </>}

      {/* ══ FEATURE FLAGS ═════════════════════════════════════ */}
      {activeTab === 'flags' && (
        <Section title="🚩 Feature Flags">
          <p className="text-muted small mb-3">Toggle features on/off for all users instantly. Changes take effect within 30 seconds (settings cache TTL).</p>
          {[
            { key: 'community',          label: 'Community Page',          desc: 'Public story community feed' },
            { key: 'photoIllustrations', label: 'Photo Illustrations',     desc: 'Upload photo for custom illustration (paid)' },
            { key: 'giftStory',          label: 'Gift a Story',            desc: 'One-time gift checkout flow' },
            { key: 'voiceCloning',       label: 'Voice Cloning',           desc: 'ElevenLabs voice clone (Family plan)' },
            { key: 'storyQuiz',          label: 'Story Quiz',              desc: 'Comprehension quiz after reading' },
            { key: 'storySeries',        label: 'Story Series',            desc: 'Multi-chapter story series' },
            { key: 'pdfExport',          label: 'PDF Export',              desc: 'Download story as formatted PDF' },
            { key: 'grandparentSharing', label: 'Grandparent Sharing',     desc: 'Share stories via private link' },
            { key: 'weeklyDigest',       label: 'Weekly Digest Emails',    desc: 'Weekly story digest for parents' },
            { key: 'bedtimeReminder',    label: 'Bedtime Reminders',       desc: 'Push / email bedtime reminder notifications' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="d-flex align-items-center justify-content-between py-3"
              style={{ borderBottom: '1px solid #f0eaf8' }}>
              <div>
                <div className="fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>{label}</div>
                <div className="text-muted" style={{ fontSize: '12px' }}>{desc}</div>
              </div>
              <div className="form-check form-switch mb-0">
                <input className="form-check-input" type="checkbox" role="switch"
                  style={{ width: '44px', height: '22px', cursor: 'pointer' }}
                  checked={!!featureFlags[key]}
                  onChange={e => setFeatureFlags(p => ({ ...p, [key]: e.target.checked }))} />
              </div>
            </div>
          ))}
          <SaveBar {...s('featureFlags')} onSave={() => save('featureFlags', featureFlags, 'Feature Flags')} label="Save Flags" />
        </Section>
      )}

      {/* ══ PLANS & LIMITS ════════════════════════════════════ */}
      {activeTab === 'limits' && <>
        {/* Plan monthly limits */}
        <Section title="📊 Monthly Limits Per Plan">
          <p className="text-muted small mb-3">Per-plan monthly allowances. Changes take effect within ~60s (or instantly on save). Top-up credits stack on top of these.</p>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0" style={{ fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--header-bg)' }}>
                  <th style={{ color: 'var(--text-main)', fontWeight: 700 }}>Feature</th>
                  <th className="text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                  <th className="text-center" style={{ color: '#6f42c1' }}>Basic</th>
                  <th className="text-center" style={{ color: '#fd7e14' }}>Family</th>
                </tr>
              </thead>
              <tbody>
                {LIMIT_ROWS.map(row => (
                  <tr key={row.key} style={{ borderBottom: '1px solid #f0eaf8' }}>
                    <td className="fw-semibold" style={{ color: 'var(--text-main)' }}>{row.label}</td>
                    {['free', 'basic', 'family'].map(plan => (
                      <td key={plan} className="text-center">
                        <input type="number" min={0} className="form-control form-control-sm text-center mx-auto"
                          style={{ maxWidth: '80px' }}
                          value={planLimits[plan]?.[row.key] ?? 0}
                          onChange={e => setPlanLimits(p => ({ ...p, [plan]: { ...p[plan], [row.key]: Number(e.target.value) } }))} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar {...s('planLimits')} onSave={() => save('planLimits', planLimits, 'Plans & Limits — Monthly Limits Per Plan')} label="Save Limits" />
        </Section>

        {/* Child profile limits */}
        <Section title="👶 Child Profile Limits">
          <p className="text-muted small mb-3">Maximum number of child profiles per plan. Family is always unlimited.</p>
          <div className="row g-3 mb-2" style={{ maxWidth: '420px' }}>
            <div className="col-6">
              <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Free plan</label>
              <input type="number" min={0} className="form-control"
                value={childLimits.free}
                onChange={e => setChildLimits(p => ({ ...p, free: Number(e.target.value) }))} />
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Basic plan</label>
              <input type="number" min={0} className="form-control"
                value={childLimits.basic}
                onChange={e => setChildLimits(p => ({ ...p, basic: Number(e.target.value) }))} />
            </div>
          </div>
          <p className="text-muted small mb-0">Family plan: <strong>Unlimited</strong></p>
          <SaveBar {...s('childProfileLimits')} onSave={() => save('childProfileLimits', childLimits, 'Plans & Limits — Child Profile Limits')} label="Save Child Limits" />
        </Section>

        {/* Top-up packs */}
        <Section title="⚡ Top-Up Packs">
          <p className="text-muted small mb-3">One-time purchase packs. <strong>Price</strong> is the USD base (auto-converted to each customer's currency at checkout). <strong>Grant</strong> is the number of credits added.</p>
          {PACK_IDS.map(id => {
            const meta = PACK_META[id];
            const cfg  = topUpConfig[id] || {};
            return (
              <div key={id} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <span style={{ minWidth: '210px', color: 'var(--text-main)', fontSize: '13px', fontWeight: 600 }}>
                  {meta.emoji} {meta.label}
                </span>
                <div className="input-group input-group-sm" style={{ maxWidth: '130px' }}>
                  <span className="input-group-text">$</span>
                  <input type="number" min={0} step="0.01" className="form-control" placeholder="Price"
                    value={cfg.usdPrice ?? ''}
                    onChange={e => setTopUpConfig(p => ({ ...p, [id]: { ...p[id], usdPrice: Number(e.target.value) } }))} />
                </div>
                <div className="input-group input-group-sm" style={{ maxWidth: '170px' }}>
                  <span className="input-group-text">+</span>
                  <input type="number" min={0} className="form-control" placeholder="Grant"
                    value={cfg.grant ?? ''}
                    onChange={e => setTopUpConfig(p => ({ ...p, [id]: { ...p[id], grant: Number(e.target.value) } }))} />
                  <span className="input-group-text" style={{ fontSize: '11px' }}>{meta.noun}</span>
                </div>
              </div>
            );
          })}
          <SaveBar {...s('topUpConfig')} onSave={() => save('topUpConfig', topUpConfig, 'Plans & Limits — Top-Up Packs')} label="Save Top-Ups" />
        </Section>

        {/* AI daily caps */}
        <Section title="🤖 AI Daily Caps">
          <p className="text-muted small mb-3">Per-day ceiling on AI calls for each cost-bearing feature (server-wide). Beyond the cap, the feature falls back to non-AI templates for the rest of the day.</p>
          {AI_CAP_ROWS.map(row => (
            <div key={row.key} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
              <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>{row.label}</span>
              <input type="number" min={0} className="form-control form-control-sm text-center"
                style={{ maxWidth: '110px' }}
                value={aiCaps[row.key] ?? 0}
                onChange={e => setAiCaps(p => ({ ...p, [row.key]: Number(e.target.value) }))} />
            </div>
          ))}
          <SaveBar {...s('aiDailyCaps')} onSave={() => save('aiDailyCaps', aiCaps, 'Plans & Limits — AI Daily Caps')} label="Save AI Caps" />
        </Section>

        {/* OTP expiry */}
        <Section title="🔐 OTP Expiry">
          <p className="text-muted small mb-3">How long a login verification code stays valid (1–120 minutes).</p>
          <div className="input-group input-group-sm" style={{ maxWidth: '200px' }}>
            <input type="number" min={1} max={120} className="form-control"
              value={otpExpiry}
              onChange={e => setOtpExpiry(Number(e.target.value))} />
            <span className="input-group-text">minutes</span>
          </div>
          <SaveBar {...s('otpExpiryMinutes')} onSave={() => save('otpExpiryMinutes', otpExpiry, 'Security — OTP Expiry (minutes)')} label="Save OTP Expiry" />
        </Section>
      </>}

      {/* ══ BUSINESS ══════════════════════════════════════════ */}
      {activeTab === 'business' && <>
        {/* Maintenance Mode */}
        <Section title="🛠️ Maintenance Mode">
          <p className="text-muted small mb-3">
            When enabled, all users (except admins) see the maintenance page. The backend also returns 503 for all non-admin API calls.
            The status refreshes within <strong>30 seconds</strong> without a restart.
          </p>
          <div className="d-flex align-items-center justify-content-between py-3 mb-3"
            style={{ borderBottom: '1px solid #f0eaf8' }}>
            <div>
              <div className="fw-semibold" style={{ color: maintenance.enabled ? '#dc3545' : '#2d1b69', fontSize: '14px' }}>
                {maintenance.enabled ? '🔴 Maintenance is ACTIVE' : '🟢 Site is live'}
              </div>
              <div className="text-muted" style={{ fontSize: '12px' }}>Toggle to put the site into maintenance mode</div>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                style={{ width: '44px', height: '22px', cursor: 'pointer' }}
                checked={!!maintenance.enabled}
                onChange={e => setMaintenance(p => ({ ...p, enabled: e.target.checked }))} />
            </div>
          </div>
          <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Message shown to users</label>
          <textarea className="form-control" rows={3}
            value={maintenance.message}
            onChange={e => setMaintenance(p => ({ ...p, message: e.target.value }))}
            placeholder="We're doing some quick maintenance. We'll be back shortly!" />
          <SaveBar {...s('maintenanceMode')} onSave={() => save('maintenanceMode', maintenance, 'Maintenance Mode')} label="Save Maintenance Settings" />
        </Section>

        {/* Contact Response Time */}
        <Section title="📧 Contact Response Time">
          <p className="text-muted small mb-3">Shown on the Contact page and in support email auto-replies.</p>
          <input className="form-control" style={{ maxWidth: '280px' }}
            value={contactTime}
            onChange={e => setContactTime(e.target.value)}
            placeholder="e.g. 24–48 hours" />
          <SaveBar {...s('contactResponseTime')} onSave={() => save('contactResponseTime', contactTime, 'Business — Contact Response Time')} />
        </Section>

        {/* App-Store Review Logins */}
        <Section title="🧪 App-Store Review Logins">
          <p className="text-muted small mb-3">
            Accounts that can sign in with a fixed code (no emailed OTP) so app-store reviewers can test the app.
            These are normal free-tier accounts with no special access — remove or rotate them after review.
          </p>
          {reviewerBypass.length === 0 && <p className="text-muted small mb-2">No review logins configured.</p>}
          {reviewerBypass.map((r, i) => (
            <div key={i} className="d-flex align-items-center gap-2 mb-2">
              <input type="email" className="form-control form-control-sm flex-grow-1" placeholder="reviewer@example.com"
                value={r.email} onChange={e => setReviewerBypass(p => p.map((x,j) => j===i ? {...x, email: e.target.value} : x))} />
              <input className="form-control form-control-sm" style={{ maxWidth: '160px' }} placeholder="Fixed code e.g. 123456"
                value={r.otp} onChange={e => setReviewerBypass(p => p.map((x,j) => j===i ? {...x, otp: e.target.value} : x))} />
              <button className="btn btn-sm btn-outline-danger rounded-2 flex-shrink-0"
                onClick={() => setReviewerBypass(p => p.filter((_,j) => j!==i))}>
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-pill mt-1"
            onClick={() => setReviewerBypass(p => [...p, { email: '', otp: '' }])}>
            <i className="bi bi-plus me-1"></i>Add Review Login
          </button>
          <SaveBar {...s('reviewerBypass')} onSave={() => save('reviewerBypass', reviewerBypass, 'Security — App-Store Review Logins')} label="Save Review Logins" />
        </Section>
      </>}

      {/* ══ AFFILIATES ════════════════════════════════════════ */}
      {activeTab === 'affiliates' && <>
        <Section title="🤝 Affiliate Program Defaults">
          <p className="text-muted small mb-3">
            These values are applied to every new affiliate when they submit an application.
            You can override them per-affiliate from the affiliate detail page.
          </p>
          <div className="row g-3 mb-3">
            <div className="col-sm-6">
              <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                Default commission % <span className="text-muted fw-normal">(what affiliate earns)</span>
              </label>
              <input
                type="number"
                className="form-control"
                min={0} max={100}
                value={affiliateDefaults.commissionPercent}
                onChange={e => setAffiliateDefaults(p => ({ ...p, commissionPercent: Number(e.target.value) }))}
              />
              <div className="form-text">New affiliates will start with this commission rate. Existing affiliates are not affected.</div>
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                Default payout threshold ($) <span className="text-muted fw-normal">(min balance for payout)</span>
              </label>
              <input
                type="number"
                className="form-control"
                min={1}
                value={affiliateDefaults.payoutThreshold}
                onChange={e => setAffiliateDefaults(p => ({ ...p, payoutThreshold: Number(e.target.value) }))}
              />
              <div className="form-text">Payout is only triggered when an affiliate's pending balance reaches this amount.</div>
            </div>
          </div>
          <SaveBar
            {...s('affiliateDefaults')}
            onSave={() => save('affiliateDefaults', affiliateDefaults, 'Affiliate Program — Default Settings')}
            label="Save Affiliate Defaults"
          />
        </Section>
      </>}

      {/* ══ SUBSCRIPTION PRICING (display-only edit) ═══════════ */}
      {activeTab === 'pricing' && (
        <Section title="💰 Subscription Pricing (USD)">
          <div className="d-flex align-items-start gap-2 mb-3 p-3 rounded-3" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <i className="bi bi-exclamation-triangle-fill mt-1" style={{ color: '#d97706', flexShrink: 0 }}></i>
            <p className="mb-0 small" style={{ color: '#92400e' }}>
              <strong>Display price only.</strong> Subscriptions are billed via fixed Stripe Price IDs (<code>STRIPE_PRICE_*</code>), so changing a number here updates what customers <em>see</em> (auto-converted to every currency) but <strong>not what Stripe actually charges</strong>. To change the charged amount, create a new Stripe Price and update its ID in the env.
            </p>
          </div>
          {SUB_PRICE_ROWS.map(row => (
            <div key={row.key} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
              <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>{row.label}</span>
              <div className="input-group input-group-sm" style={{ maxWidth: '140px' }}>
                <span className="input-group-text">$</span>
                <input type="number" min={0} step="0.01" className="form-control"
                  value={subPricing[row.key] ?? ''}
                  onChange={e => setSubPricing(p => ({ ...p, [row.key]: Number(e.target.value) }))} />
              </div>
            </div>
          ))}
          <SaveBar {...s('subscriptionPricing')} onSave={() => save('subscriptionPricing', subPricing, 'Subscription Pricing (USD, display)')} label="Save Pricing" />
        </Section>
      )}

      {/* ══ FREE TRIAL ════════════════════════════════════════ */}
      {activeTab === 'pricing' && (
        <Section title="🎁 Free Trial">
          <p className="text-muted small mb-3">
            A card-required free trial. New subscribers on the chosen plan enter a card, aren't charged during the trial,
            and auto-convert when it ends (unless they cancel first). Each account can only trial once. Changes apply within ~60s.
          </p>

          {/* Enabled toggle */}
          <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
            <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>Trial enabled</span>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                checked={!!trialConfig.enabled}
                onChange={e => setTrialConfig(c => ({ ...c, enabled: e.target.checked }))} />
            </div>
          </div>

          {/* Trial length */}
          <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
            <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>Trial length (days)</span>
            <div className="input-group input-group-sm" style={{ maxWidth: '140px' }}>
              <input type="number" min={1} max={90} step="1" className="form-control"
                value={trialConfig.days ?? 7}
                onChange={e => setTrialConfig(c => ({ ...c, days: Number(e.target.value) }))} />
              <span className="input-group-text">days</span>
            </div>
          </div>

          {/* Plans */}
          <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
            <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>Trial applies to plan(s)</span>
            <div className="d-flex gap-3">
              {['basic', 'family'].map(p => {
                const selected = (trialConfig.plans || []).includes(p);
                return (
                  <div key={p} className="form-check mb-0">
                    <input className="form-check-input" type="checkbox" id={`trial-plan-${p}`}
                      checked={selected}
                      onChange={e => setTrialConfig(c => {
                        const set = new Set(c.plans || []);
                        if (e.target.checked) set.add(p); else set.delete(p);
                        return { ...c, plans: Array.from(set) };
                      })} />
                    <label className="form-check-label" htmlFor={`trial-plan-${p}`}
                      style={{ fontSize: '13px', color: 'var(--text-main)', textTransform: 'capitalize' }}>{p}</label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Requires card */}
          <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0eaf8' }}>
            <span style={{ color: 'var(--text-main)', fontSize: '13px' }}>
              Require card up front
              <span className="d-block text-muted" style={{ fontSize: '11px' }}>Higher conversion; auto-charges at trial end</span>
            </span>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                checked={!!trialConfig.requiresCard}
                onChange={e => setTrialConfig(c => ({ ...c, requiresCard: e.target.checked }))} />
            </div>
          </div>

          <SaveBar {...s('trialConfig')} onSave={() => save('trialConfig', trialConfig, 'Free Trial')} label="Save Trial Settings" />
        </Section>
      )}

      {/* ══ RATE LIMITS ═══════════════════════════════════════ */}
      {activeTab === 'ratelimits' && (
        <Section title="🛡️ Rate Limits">
          <p className="text-muted small mb-3">
            Per-route request caps. <strong>Window</strong> is the time bucket and <strong>Max</strong> is the requests allowed per IP within it. Changes apply within ~60s — no restart. Tighten to cut abuse/cost; loosen if real users hit limits. Take care loosening AI &amp; payment routes.
          </p>
          {(() => {
            const meta = envConfig?.rateLimitMeta || {};
            const groups = {};
            Object.entries(meta).forEach(([key, m]) => { (groups[m.group] = groups[m.group] || []).push([key, m]); });
            return Object.entries(groups).map(([group, items]) => (
              <div key={group} className="mb-3">
                <div className="fw-bold mb-2" style={{ color: '#6f42c1', fontSize: '13px' }}>{group}</div>
                {items.map(([key, m]) => {
                  const cfg = rateLimitsCfg[key] || { windowMs: m.windowMs, max: m.max };
                  const win = cfg.windowMs ?? m.windowMs;
                  return (
                    <div key={key} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <span style={{ minWidth: '200px', color: 'var(--text-main)', fontSize: '13px' }}>{m.label}</span>
                      <div className="input-group input-group-sm" style={{ maxWidth: '150px' }}>
                        <span className="input-group-text" style={{ fontSize: '11px' }}>win ms</span>
                        <input type="number" min={1000} step={1000} className="form-control"
                          value={win}
                          onChange={e => setRateLimitsCfg(p => ({ ...p, [key]: { ...(p[key] || { max: m.max }), windowMs: Number(e.target.value) } }))} />
                      </div>
                      <span className="text-muted" style={{ fontSize: '11px', minWidth: '32px' }}>{msLabel(win)}</span>
                      <div className="input-group input-group-sm" style={{ maxWidth: '120px' }}>
                        <span className="input-group-text" style={{ fontSize: '11px' }}>max</span>
                        <input type="number" min={1} className="form-control"
                          value={cfg.max ?? m.max}
                          onChange={e => setRateLimitsCfg(p => ({ ...p, [key]: { ...(p[key] || { windowMs: m.windowMs }), max: Number(e.target.value) } }))} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })()}
          <SaveBar {...s('rateLimits')} onSave={() => save('rateLimits', rateLimitsCfg, 'Rate Limits (per route)')} label="Save Rate Limits" />
        </Section>
      )}

      {/* ══ CRON JOBS ═════════════════════════════════════════ */}
      {activeTab === 'cron' && (
        <Section title="⏰ Cron Schedules">
          <p className="text-muted small mb-3">
            Background-job timings as <a href="https://crontab.guru" target="_blank" rel="noopener noreferrer">cron expressions</a> (server timezone). Changes apply within ~60s — no restart. An invalid expression is ignored and the job keeps its default schedule.
          </p>
          {Object.entries(envConfig?.cronMeta || {}).map(([key, m]) => (
            <div key={key} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <span style={{ minWidth: '230px', color: 'var(--text-main)', fontSize: '13px' }}>{m.label}</span>
              <input className="form-control form-control-sm" style={{ maxWidth: '180px', fontFamily: 'monospace' }}
                placeholder={m.defaultSchedule}
                value={cronCfg[key] ?? m.defaultSchedule}
                onChange={e => setCronCfg(p => ({ ...p, [key]: e.target.value }))} />
              <span className="text-muted" style={{ fontSize: '11px' }}>default: <code>{m.defaultSchedule}</code></span>
            </div>
          ))}
          <SaveBar {...s('cronSchedules')} onSave={() => save('cronSchedules', cronCfg, 'Cron Schedules')} label="Save Schedules" />
        </Section>
      )}
    </AdminLayout>
  );
};

export default AdminSettings;
